import * as React from 'react';
import './loss-and-profit.scss';
import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { Table } from 'components/table/table';
import { PaginationList } from 'core/domain';
import { Badge } from 'components';
import { CopyOutlined, DollarCircleOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Button, buttonType } from 'components/button/button';
import { Input } from 'components/input/input';
import { Enum } from '../../../../core/domain/commons/common';
import moment from 'jalali-moment';
import { digitSperator, faNumToEnNum } from 'core/utils/decimalExtentions';
import { copyToClipboard } from 'core/utils/copyToClipboard';
import { getIconPath } from 'core/utils/getIconPath';
import { Modal } from 'components/modal/modal';
import { Dropdown } from '../../../../components/dropdown/dropdown';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { getOrderTransactionStateBadgeType } from '../orders-transactions/script';
import { getLossAndProfitReport } from 'core/repositories/balancer-transaction';
import { LossAndProfit, LossAndProfitReport } from 'core/domain/balancer-transaction/balancer-transaction';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import ExportData from 'highcharts/modules/export-data';
import { Spin } from 'antd';
import HighchartsReact from 'highcharts-react-official';
import { jFormat } from 'core/utils/jFormat';

Exporting(Highcharts);
ExportData(Highcharts);

interface FilterValues {
    startDate: string;
    endDate: string;
}

const empty_filters = {
    search: '',
    state: {
        id: -2,
        title: 'همه وضعیت ها',
    },
    type: {
        id: -1,
        title: 'همه نوع ها',
    },
    marketType: {
        id: 0,
        title: 'همه ارز ها',
    },
    startDate: moment().locale('fa').subtract(10, 'days').format('jYYYY/jMM/jDD'),
    endDate: '',
};

export const LossAndProfitReportPage: React.FC = () => {
    const [filterValues, setFilterValues] = React.useState<FilterValues>(empty_filters);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = React.useState<boolean>(false);
    const [deleteButtonLoading, setDeleteButtonLoading] = React.useState<boolean>(false);
    const [cardLoading, setCardLoading] = React.useState<boolean>(false);
    const [report, setReport] = React.useState<LossAndProfitReport>();
    const [chartOptions, setChartOptions] = React.useState<Highcharts.Options[]>([]);
    const [query, setQuery] = React.useState<string>('');
    const [cryptoDollarPage, setCryptoDollarPage] = React.useState<number>(1);
    const [usdtPage, setUsdtPage] = React.useState<number>(1);
    const [uusdPage, setUusdPage] = React.useState<number>(1);
    const pageSize = 10;
    const [initialQuery, setInitialQuery] = React.useState('');

    const startDateRef = React.useRef();
    const endDateRef = React.useRef();

    const navigate = useNavigate();

    const buildChartOptions = (reportData: LossAndProfitReport) => {
        const chartList: Highcharts.Options[] = [];
        const types: { key: keyof LossAndProfitReport; name: string }[] = [
            { key: 'cryptoDollar', name: 'کریپتو دلار' },
            { key: 'usdt', name: 'تتر' },
            { key: 'uusd', name: 'یوتوپیا' },
        ];

        types.forEach(({ key, name }) => {
            const seriesData =
                reportData[key]?.map((item: LossAndProfit) => ({
                    date: moment(item.dateTime).locale('fa').local().format('jYYYY/MM/DD'),
                    ...item,
                })) || [];

            const dates = seriesData.map((d) => d.date);

            chartList.push({
                chart: {
                    className: 'pmx-chart',
                    style: {
                        fontFamily: 'YekanBakh',
                        fontSize: '16px',
                    },
                },
                title: { text: `میانگین خرید و فروش - ${name}` },
                xAxis: { categories: dates },
                yAxis: {
                    title: { text: 'میانگین' },
                    labels: {
                        formatter: function () {
                            const val = this.value as number;
                            if (val >= 1_000_000) return val / 1_000_000 + 'M';
                            if (val >= 1_000) return val / 1_000 + 'K';
                            return val.toString();
                        },
                    },
                },
                tooltip: {
                    style: {
                        fontSize: '16px',
                    },
                    formatter: function () {
                        const points = this.points || [];
                        return (
                            '<b>' +
                            this.x +
                            '</b><br>' +
                            points
                                .map(
                                    (p) =>
                                        '<span style="color:' +
                                        p.color +
                                        '"><span>' +
                                        p.series.name +
                                        '</span>: <b>' +
                                        Highcharts.numberFormat(p.y ?? 0, 0, '.', ',') +
                                        '</b></span>',
                                )
                                .join('<br>')
                        );
                    },
                    shared: true,
                },
                series: [
                    {
                        name: 'میانگین فروش',
                        data: seriesData.map((d) => d.sellAverage),
                        type: 'spline',
                        color: '#F44336',
                    },
                    {
                        name: 'میانگین خرید',
                        data: seriesData.map((d) => d.buyAverage),
                        type: 'spline',
                        color: '#4CAF50',
                    },
                ],
                credits: { enabled: false },
                exporting: {
                    enabled: true,
                    buttons: {
                        contextButton: {
                            menuItems: [
                                'downloadPNG',
                                'downloadJPEG',
                                'downloadPDF',
                                'downloadSVG',
                                'downloadCSV',
                                'downloadXLS',
                            ],
                        },
                    },
                },
            });

            chartList.push({
                chart: {
                    className: 'pmx-chart',
                    style: {
                        fontFamily: 'YekanBakh',
                        fontSize: '16px',
                    },
                },
                title: { text: `مجموع خرید و فروش - ${name}` },
                xAxis: { categories: dates },
                yAxis: {
                    title: { text: 'مقدار ($)' },
                    labels: {
                        formatter: function () {
                            const val = this.value as number;
                            if (val >= 1_000_000) return val / 1_000_000 + 'M';
                            if (val >= 1_000) return val / 1_000 + 'K';
                            return val.toString();
                        },
                    },
                },
                tooltip: {
                    style: {
                        fontSize: '16px',
                    },
                    formatter: function () {
                        const points = this.points || [];
                        return (
                            '<b>' +
                            this.x +
                            '</b><br>' +
                            points
                                .map(
                                    (p) =>
                                        '<span style="color:' +
                                        p.color +
                                        '"><span>' +
                                        p.series.name +
                                        '</span>: <b>' +
                                        Highcharts.numberFormat(p.y ?? 0, 0, '.', ',') +
                                        '</b></span>',
                                )
                                .join('<br>')
                        );
                    },
                    shared: true,
                },
                series: [
                    {
                        name: 'مجموع خرید ($)',
                        data: seriesData.map((d) => d.totalBuyInDollar),
                        type: 'spline',
                        color: '#2196F3',
                    },
                    {
                        name: 'مجموع فروش ($)',
                        data: seriesData.map((d) => d.totalSellInDollar),
                        type: 'spline',
                        color: '#FFC107',
                    },
                ],
                credits: { enabled: false },
                exporting: {
                    enabled: true,
                    buttons: {
                        contextButton: {
                            menuItems: [
                                'downloadPNG',
                                'downloadJPEG',
                                'downloadPDF',
                                'downloadSVG',
                                'downloadCSV',
                                'downloadXLS',
                            ],
                        },
                    },
                },
            });
        });

        setChartOptions(chartList);
    };

    const filterHandle = (clearFilter?: boolean) => {
        var _query = ``;

        if (filterValues.startDate !== '')
            _query += `${_query.includes('?') ? '&' : '?'}startDate=${jFormat(filterValues.startDate)}`;
        if (filterValues.endDate !== '')
            _query += `${_query.includes('?') ? '&' : '?'}endDate=${jFormat(filterValues.endDate)}`;

        setQuery(_query);
        getLossAndProfitReport(
            clearFilter ? setDeleteButtonLoading : setLoading,
            setReport,
            clearFilter ? '' : _query,
            (res) => {
                buildChartOptions(res);
            },
        );
    };

    React.useEffect(() => {
        filterHandle();
    }, []);

    const clearFiltersHandle = () => {
        setFilterValues({ ...empty_filters, startDate: '' });
        filterHandle(true);
    };

    return (
        <Page className="pmx-loss-and-profit" title="گزارشات سود و زیان">
            <Card blur={loading}>
                <div className="filter-row">
                    <div className="fields">
                        <DatePicker
                            className="date-picker"
                            value={filterValues.startDate}
                            calendar={persian}
                            ref={startDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.startDate}
                                        icon={<CalendarIcon />}
                                        placeholder="از تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({ ...filterValues, startDate: e.format() });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.endDate}
                            calendar={persian}
                            ref={endDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.endDate}
                                        icon={<CalendarIcon />}
                                        placeholder="تا تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({ ...filterValues, endDate: e.format() });
                            }}
                        />
                    </div>
                    <div className="buttons">
                        <Button
                            loading={buttonLoading}
                            disabled={buttonLoading}
                            text="جستجو"
                            type={buttonType.success}
                            onClick={() => filterHandle()}
                        />
                        <Button
                            loading={deleteButtonLoading}
                            disabled={deleteButtonLoading}
                            text="حذف فیلتر"
                            type={buttonType.error}
                            onClick={clearFiltersHandle}
                        />
                    </div>
                </div>
                {report && (
                    <>
                        {(['cryptoDollar', 'usdt', 'uusd'] as Array<keyof LossAndProfitReport>).map((key, index) => (
                            <div key={key} className="data-section">
                                <Card className="chart-block w-full lg:w-[calc(50%-0.5rem)]">
                                    <HighchartsReact highcharts={Highcharts} options={chartOptions[index * 2]} />
                                </Card>
                                <Card className="chart-block w-full lg:w-[calc(50%-0.5rem)]">
                                    <HighchartsReact highcharts={Highcharts} options={chartOptions[index * 2 + 1]} />
                                </Card>
                            </div>
                        ))}
                    </>
                )}
            </Card>
        </Page>
    );
};
